import { createContext, useContext, type ReactNode } from 'react';
import { resolveFilePathWithinRoot } from '@reflex/shared';

/**
 * The directory the surrounding content's relative paths are relative to.
 *
 * A file-preview affordance is reached through the content-scanner registry,
 * which only claims *absolute* paths — a relative token is indistinguishable
 * from prose. But an agent writes paths relative to where it runs, so without
 * the directory it ran in, most of the paths a transcript shows can't be turned
 * into anything openable. The surface that owns a session (the SDK chat view)
 * knows that directory and provides it once here; {@link ScannedPath} and any
 * caller deciding whether to render a path as an affordance read it back.
 *
 * The sibling of `MarkdownAgentIdProvider`, and for the same reason: scanner
 * components need facts about the agent whose content this is, and they render
 * far from anything that knows them. When absent, relative paths render as the
 * plain text they already were.
 */
const WorkingDirContext = createContext<string | null>(null);

export function WorkingDirProvider({
  workingDir,
  children,
}: {
  /** Absolute directory, or null when the session never announced one. */
  workingDir: string | null;
  children: ReactNode;
}) {
  return <WorkingDirContext.Provider value={workingDir}>{children}</WorkingDirContext.Provider>;
}

/** Working directory for the surrounding content, or null when unknown. */
export function useWorkingDir(): string | null {
  return useContext(WorkingDirContext);
}

/**
 * Characters that mean the token is not a final path: a glob or brace a shell
 * would have expanded, a `$` substitution shown as written, or whitespace —
 * which in a rewritten row means several operands joined into one phrase
 * ("a.ts and b.ts", "3 paths"), not a file with a space in its name.
 */
const NOT_A_LITERAL_PATH = /[*?[\]{}$\s]/;

/**
 * The absolute path a preview affordance can be pointed at, or null when the
 * token isn't one we can resolve honestly.
 *
 * Narrow on purpose: a path we decline to resolve costs a link, while a path
 * resolved wrongly points a reader at a file nobody touched. `~` is refused
 * because the home directory is not something the content records, and a
 * relative path with no known working directory is refused rather than guessed
 * at from the root.
 *
 * The result is normalized either way, so a tool that reported
 * `/home/user//app/` and one that reported `/home/user/app` resolve to the one
 * token a scanner matches and a preview can be keyed on. Normalization is
 * lexical — `.` and `..` collapse without consulting a filesystem, the same
 * trade `normalizeFilePath` already documents — and a token that climbs above
 * the root (`/../etc/hosts`) is refused rather than clamped to a file it never
 * named; see `resolveFilePathWithinRoot`.
 */
export function absoluteFilePath(path: string, workingDir: string | null): string | null {
  if (!path || NOT_A_LITERAL_PATH.test(path) || path.startsWith('~')) return null;
  if (path.startsWith('/')) return resolveFilePathWithinRoot(path);
  if (!workingDir?.startsWith('/')) return null;
  return resolveFilePathWithinRoot(path, workingDir);
}
